import type { Reducer } from 'react'
import { match } from 'ts-pattern'
import type {
  CubeActions,
  PostActionStage,
  CubeReducerState
} from 'modules/Cube/domain/cube.domain.types'
import * as actions from 'modules/Cube/domain/actionHandlers'
import * as postActionStages from 'modules/Cube/domain/postActionStages'
import { INITIAL_CUBE_STATE } from 'modules/Cube/domain/cube.constants'
import { getResolvedPhaseData } from 'modules/Cube/domain/postActionStages/queries/getResolvedPhaseData'
import { cubeDeveloperLogGroup } from 'modules/Cube/utils/cubeDeveloperLog'
import compose from 'lodash/fp/compose'

const handleAction = (prevState: CubeReducerState) => (action: CubeActions) => {
  return match(action)
    .with({ type: 'load' }, matchedAction =>
      actions.load(prevState)(matchedAction)
    )
    .with({ type: 'reset' }, matchedAction =>
      actions.reset(prevState)(matchedAction)
    )
    .with({ type: 'updateData' }, matchedAction =>
      actions.updateData(prevState)(matchedAction)
    )
    .with({ type: 'updateConfiguration' }, matchedAction =>
      actions.updateConfiguration(prevState)(matchedAction)
    )
    .with({ type: 'updateEditor' }, matchedAction =>
      actions.updateEditor(prevState)(matchedAction)
    )
    .with({ type: 'deleteData' }, matchedAction =>
      actions.deleteData(prevState)(matchedAction)
    )
    .with({ type: 'alignPhaseDuration' }, matchedAction =>
      actions.alignPhaseDuration(prevState)(matchedAction)
    )
    .exhaustive()
}

const logStage =
  (stageName: string): PostActionStage =>
  () =>
  prevState => {
    cubeDeveloperLogGroup({
      summary: `Post-action stage complete - ${stageName}`,
      entries: [{ label: 'State', value: () => prevState }]
    })
    return prevState
  }

const handlePostActionStages: PostActionStage = ctx => prevState => {
  /**
   * Since this is a compose, the actual order is bottom to top!
   */
  return compose(
    logStage('Validation')(ctx),
    postActionStages.validation(ctx),
    logStage('Queries')(ctx),
    postActionStages.queries(ctx),
    logStage('Cleanup')(ctx),
    postActionStages.cleanup(ctx),
    logStage('Update Price Currencies')(ctx),
    postActionStages.updatePriceCurrencies(ctx)
  )(prevState) as CubeReducerState
}

export const billingScheduleReducer: Reducer<CubeReducerState, CubeActions> = (
  prevState = INITIAL_CUBE_STATE,
  action
) => {
  cubeDeveloperLogGroup({
    summary: `Reducer started - ${action.type}`,
    entries: [
      { label: 'Initial state', value: () => prevState },
      { label: 'Action', value: () => action }
    ]
  })

  const postActionState = handleAction(prevState)(action)

  cubeDeveloperLogGroup({
    summary: `Post-action stages started - ${action.type}`,
    entries: [{ label: 'State', value: () => postActionState }]
  })

  const finalState = handlePostActionStages({
    preActionState: prevState,
    action
  })(postActionState)

  /**
   * Post-action stages (e.g. cleanup) may normalize `data` after load. Snapshot
   * `initialData` from the final state so save availability reflects user edits,
   * not load-time normalization.
   */
  const stateAfterAction =
    action.type === 'load'
      ? {
          ...finalState,
          initialData: finalState.data,
          initialQueries: {
            resolvedPhases: getResolvedPhaseData(finalState)
          }
        }
      : finalState

  cubeDeveloperLogGroup({
    summary: `Reducer complete - ${action.type}`,
    entries: [{ label: 'Final state', value: () => stateAfterAction }]
  })

  return stateAfterAction
}
